import React, { useEffect, useState, useRef } from 'react';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

export function TikTokBusinessCallback() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Connecting TikTok Business account...');

  // CRITICAL FIX: Prevent double execution in React Strict Mode
  const calledRef = useRef(false);

  useEffect(() => {
    // Guard against double execution
    if (calledRef.current) return;
    calledRef.current = true;

    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const authCode = params.get('auth_code');
      const error = params.get('error');

      if (error) {
        setStatus('error');
        setMessage(`Authorization failed: ${error}`);
        toast.error(`TikTok Business authorization failed: ${error}`);
        setTimeout(() => {
          const redirectUrl = localStorage.getItem('tiktok_business_auth_redirect') || '/social';
          window.location.href = redirectUrl;
        }, 3000);
        return;
      }

      if (!authCode) {
        setStatus('error');
        setMessage('No authorization code received');
        toast.error('No authorization code received');
        setTimeout(() => {
          const redirectUrl = localStorage.getItem('tiktok_business_auth_redirect') || '/social';
          window.location.href = redirectUrl;
        }, 3000);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setStatus('error');
        setMessage('You must be logged in');
        toast.error('You must be logged in');
        setTimeout(() => window.location.href = '/', 2000);
        return;
      }

      setMessage('Exchanging authorization code...');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/tiktok-business-oauth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          authCode,
          userId: user.id,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to connect TikTok Business account');
      }

      setStatus('success');
      setMessage(`Successfully connected: ${data.account_name}`);
      toast.success(`TikTok Business account connected: ${data.account_name}`);

      localStorage.removeItem('tiktok_business_auth_redirect');

      setTimeout(() => {
        window.location.href = '/social';
      }, 2000);
    } catch (error) {
      console.error('TikTok Business callback error:', error);
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Failed to connect account');
      toast.error('Failed to connect TikTok Business account');

      setTimeout(() => {
        const redirectUrl = localStorage.getItem('tiktok_business_auth_redirect') || '/social';
        window.location.href = redirectUrl;
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
      <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full">
        <div className="flex flex-col items-center space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
              <h2 className="text-xl font-semibold text-white">Connecting Account</h2>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-12 h-12 text-green-500" />
              <h2 className="text-xl font-semibold text-white">Success!</h2>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-12 h-12 text-red-500" />
              <h2 className="text-xl font-semibold text-white">Connection Failed</h2>
            </>
          )}

          <p className="text-gray-400 text-center">{message}</p>

          {status !== 'loading' && (
            <p className="text-sm text-gray-500">Redirecting...</p>
          )}
        </div>
      </div>
    </div>
  );
}
